import { PieChart, Pie, Cell } from "recharts";
import { getSelectedPerformanceYearData, numMonthsBetweenDates } from "../../util/period";
import { PeriodsProgressCircleLabelOverlay } from "./PeriodsCircleOverlay/PeriodsCircleOverlay";
import { CHART_COLORS, PeriodsProgressCircleProps } from "./ChartSettings";
import { PeriodCircleLegend } from "./PeriodsCircleLegend/PeriodsCircleLegend";
import "./PeriodsProgressCircle.scss";

const calculateFillProgress = ({
  targetAmount,
  currentAmount,
  tracking,
  disabled,
  period,
}: PeriodsProgressCircleProps) => {
  const { selectedPYStartPeriod } = getSelectedPerformanceYearData(period);
  const periodsFromPYStart = numMonthsBetweenDates(selectedPYStartPeriod, period);

  const volumePerSlice = targetAmount.actual / 12;
  const achievedFillProgress = currentAmount.actual / volumePerSlice;

  const offTrackFillProgress =
    periodsFromPYStart >= achievedFillProgress ? periodsFromPYStart - achievedFillProgress : 0;
  const remainingFillProgress = 12 - achievedFillProgress - offTrackFillProgress;

  // Define exempt colors
  const EXEMPT_COLORS = {
    achieved: "#5e5e5e", // Dark gray for On Track
    notTracking: "#949494", // Medium gray for Below Target
    offTrack: "#d6d6d6", // Light gray for Desired Target
    default: "#f4f4f4", // Very light gray for periods not started yet
  };

  const data = disabled
    ? [
        {
          name: "achievedSlice",
          value: achievedFillProgress,
          color: tracking ? EXEMPT_COLORS.achieved : EXEMPT_COLORS.notTracking,
        },
        { name: "offTrackSlice", value: offTrackFillProgress, color: EXEMPT_COLORS.offTrack },
        {
          name: "remainingSlice",
          value: remainingFillProgress < 0 ? 0 : remainingFillProgress,
          color: EXEMPT_COLORS.default,
        },
      ]
    : [
        {
          name: "achievedSlice",
          value: achievedFillProgress,
          color: tracking ? CHART_COLORS.achieved : CHART_COLORS.notTracking,
        },
        { name: "offTrackSlice", value: offTrackFillProgress, color: CHART_COLORS.offTrack },
        {
          name: "remainingSlice",
          value: remainingFillProgress < 0 ? 0 : remainingFillProgress,
          color: CHART_COLORS.default,
        },
      ];

  return { data, achievedFillProgress };
};

export const PeriodsProgressCircle = ({
  targetAmount,
  currentAmount,
  tracking,
  disabled,
  period,
  showLegend = true,
  chartTitle,
}: PeriodsProgressCircleProps) => {
  const fillProgress = calculateFillProgress({
    targetAmount,
    currentAmount,
    tracking,
    disabled,
    period,
  });
  return (
    <div className="PeriodsProgressCircle">
      <div className="PeriodsProgressCircle__chartWrapper">
        <PeriodsProgressCircleLabelOverlay
          {...{ targetAmount, currentAmount, tracking, disabled, period, chartTitle }}
          numFilledSlices={fillProgress.achievedFillProgress}
          currentVolumeFill={
            disabled ? (tracking ? "#5e5e5e" : "#949494") : tracking ? CHART_COLORS.achieved : CHART_COLORS.notTracking
          }
        />
        <div style={{ position: "relative", width: 222, height: 222 }}>
          <PieChart width={222} height={222}>
            <Pie
              data={fillProgress.data}
              cx="50%"
              cy="50%"
              startAngle={90}
              endAngle={-270}
              innerRadius={70}
              outerRadius={100}
              dataKey="value"
              animationBegin={0}
              animationDuration={0}
              isAnimationActive={false}
              labelLine={false}
              legendType="none"
            >
              {fillProgress.data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={0} />
              ))}
            </Pie>
          </PieChart>
        </div>
      </div>
      {showLegend && <PeriodCircleLegend disabled={disabled} />}
    </div>
  );
};
